import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, MessageCircleMore, Settings2 } from "lucide-react";
import { Button } from "ui/Button";
import { ScrollArea } from "ui/ScrollArea";
import LoadingSkeleton from "ui/LoadingSkeleton";
import EmptyState from "ui/EmptyState";
import ActorAvatar from "components/Actors/ActorAvatar";
import ActorName from "components/Actors/ActorName";
import { ACTOR_AVATAR_SIZES } from "util/actorAvatarSizes";
import { useUser } from "util/UserContext";
import {
  useChatConversationQuery,
  useChatMessagesQuery,
  useMarkChatReadMutation,
} from "hooks/queries/useChatQueries";
import { useSocket } from "hooks/useSocket";
import type { ChatMessage, SharedEntityMeta } from "types/chat";
import ChatMessageCard from "./ChatMessage";
import ChatComposer from "./ChatComposer";
import ChatThread from "./ChatThread";
import ChatSettings from "./ChatSettings";
import ContentViewerOverlay from "./ContentViewerOverlay";
import {
  getConversationActor,
  getConversationSubtitle,
  getConversationTitle,
} from "./utils";

interface ChatConversationProps {
  conversationPublicId: string;
  onBack?: () => void;
}

const ChatConversation: React.FC<ChatConversationProps> = ({ conversationPublicId, onBack }) => {
  const { data: conversation, isLoading: conversationLoading } = useChatConversationQuery(conversationPublicId, true);
  const { data: messages, isLoading: messagesLoading } = useChatMessagesQuery(conversationPublicId, true);
  const markReadMutation = useMarkChatReadMutation(conversationPublicId);
  const { joinConversation } = useSocket();
  const { currentUser } = useUser();
  const [replyToMessage, setReplyToMessage] = useState<ChatMessage | null>(null);
  const [threadRootMessage, setThreadRootMessage] = useState<ChatMessage | null>(null);
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const [selectedSharedEntity, setSelectedSharedEntity] = useState<SharedEntityMeta | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  const lastMarkedMessageRef = useRef<string | null>(null);

  useEffect(() => {
    if (conversationPublicId) {
      joinConversation(conversationPublicId);
    }
  }, [conversationPublicId, joinConversation]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages?.length]);

  const latestMessagePublicId = useMemo(() => {
    if (!messages || messages.length === 0) {
      return null;
    }
    return messages[messages.length - 1]?.public_id || null;
  }, [messages]);

  useEffect(() => {
    if (
      !latestMessagePublicId
      || markReadMutation.isPending
      || lastMarkedMessageRef.current === latestMessagePublicId
    ) {
      return;
    }
    lastMarkedMessageRef.current = latestMessagePublicId;
    void markReadMutation.mutateAsync(latestMessagePublicId);
  }, [latestMessagePublicId, markReadMutation]);

  if (conversationLoading || !conversation) {
    return (
      <div className="flex h-full flex-col">
        <div className="border-b border-eoe-border-main p-4 dark:border-eoe-dark-border-main">
          <LoadingSkeleton height="h-5" width="w-40" rounded="rounded-full" />
        </div>
        <div className="flex-1 p-4">
          <LoadingSkeleton variant="comment-card" count={4} />
        </div>
      </div>
    );
  }

  const title = getConversationTitle(conversation, "Conversation");
  const subtitle = getConversationSubtitle(conversation);
  const actor = getConversationActor(conversation);
  const isPendingRequest = conversation.request?.status === "pending";
  const isRequester = conversation.request?.requester_user_id === currentUser?.id;

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-3 border-b border-eoe-border-main p-4 dark:border-eoe-dark-border-main">
        <div className="flex min-w-0 items-center gap-2">
          {onBack ? (
            <Button variant="ghost" size="icon" onClick={onBack} aria-label="Back to chats">
              <ArrowLeft />
            </Button>
          ) : null}
          <ActorAvatar actor={actor} size={ACTOR_AVATAR_SIZES.header} />
          <div className="min-w-0">
            {actor ? (
              <ActorName
                actor={actor}
                label={title}
                className="truncate font-semibold text-eoe-text dark:text-eoe-dark-text"
              />
            ) : (
              <div className="truncate font-semibold text-eoe-text dark:text-eoe-dark-text">{title}</div>
            )}
            <div className="truncate text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">{subtitle}</div>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={() => setSettingsOpen(true)} aria-label="Conversation settings">
          <Settings2 />
        </Button>
      </div>

      {isPendingRequest ? (
        <div className="border-b border-eoe-border-main bg-eoe-bg-hover px-4 py-2 text-sm text-eoe-text-sub dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-hover dark:text-eoe-dark-text-sub">
          {isRequester
            ? "This chat request is waiting for approval."
            : "Review this request in the Requests tab before replying."}
        </div>
      ) : null}

      <ScrollArea className="flex-1">
        <div className="space-y-4 p-4">
          {messagesLoading ? (
            <LoadingSkeleton variant="comment-card" count={5} />
          ) : messages && messages.length > 0 ? (
            messages.map((message) => (
              <ChatMessageCard
                key={message.public_id}
                conversation={conversation}
                message={message}
                onReply={(selectedMessage) => setReplyToMessage(selectedMessage)}
                onOpenThread={(selectedMessage) => setThreadRootMessage(selectedMessage)}
                onOpenSharedEntity={(sharedEntity) => setSelectedSharedEntity(sharedEntity)}
              />
            ))
          ) : (
            <EmptyState
              icon={<MessageCircleMore />}
              title="No messages yet"
              subtitle="Start the conversation below."
            />
          )}
          <div ref={endRef} />
        </div>
      </ScrollArea>

      {!isPendingRequest ? (
        <div className="border-t border-eoe-border-main p-4 dark:border-eoe-dark-border-main">
          <ChatComposer
            conversationPublicId={conversation.public_id}
            replyToMessage={replyToMessage}
            onCancelReply={() => setReplyToMessage(null)}
            onSent={() => setReplyToMessage(null)}
          />
        </div>
      ) : null}

      <ChatThread
        conversation={conversation}
        threadRootMessage={threadRootMessage}
        isOpen={threadRootMessage !== null}
        onClose={() => setThreadRootMessage(null)}
      />

      <ChatSettings
        conversation={conversation}
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />

      <ContentViewerOverlay
        sharedEntity={selectedSharedEntity}
        isOpen={selectedSharedEntity !== null}
        onClose={() => setSelectedSharedEntity(null)}
      />
    </div>
  );
};

export default ChatConversation;
